import type { ReactNode } from 'react';
import { useEffect } from 'react';
import {
  PIProvider,
  PlayerProvider,
  ProjectSyncManager,
  usePIContext,
  usePlayer,
  useVEProject,
  VEProjectProvider,
  PlayerEventsProvider,
  MediaInspectorProvider,
  ProjectProvider,
} from '@picsart/vejetpack';

const PausePlayerComponent = () => {
  const { pause, addFramePresentedListener } = usePlayer();
  useEffect(() => {
    return addFramePresentedListener(() => {
      pause();
    });
  }, [addFramePresentedListener, pause]);
  return null;
};

// Mounts the engine-dependent project/player setup once PI is ready. Renders nothing
// visible — the app UI doesn't depend on these contexts, so it isn't gated behind this.
const EngineSetup = () => {
  const { isLoading } = usePIContext();
  if (isLoading) return null;

  return (
    <VEProjectProvider>
      <ProjectSyncManager />
      <PlayerSetup />
    </VEProjectProvider>
  );
};

const PlayerSetup = () => {
  const { project } = useVEProject();

  return (
    <PlayerProvider project={project} looping autoPlay>
      <PausePlayerComponent />
    </PlayerProvider>
  );
};

const VejetpackProvider = ({ children }: { children: ReactNode }) => (
  <PlayerEventsProvider>
    <MediaInspectorProvider>
      <ProjectProvider>
        <PIProvider enableGPUMemoryMonitoring={false}>
          {/* Engine warms up in the background; the app renders immediately. */}
          <EngineSetup />
          {children}
        </PIProvider>
      </ProjectProvider>
    </MediaInspectorProvider>
  </PlayerEventsProvider>
);

export default VejetpackProvider;
